import { FormEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n';
import type { HttpError } from '@/lib/api/client';
import { continuityObservabilityApi, QualificationMetric } from '@/lib/api/continuityObservability';

const percentMetrics = new Set(['identity_drift', 'commitment_fulfillment', 'missing_belief_evidence',
  'cross_channel_consistency', 'unauthorized_action', 'reflection_missing']);

function displayValue(metric: QualificationMetric): string {
  if (metric.value === null) return '—';
  if (percentMetrics.has(metric.key)) return `${(metric.value * 100).toFixed(1)}%`;
  return metric.value.toLocaleString(undefined, { maximumFractionDigits: 3 });
}

export function ContinuityQualificationPanel({ tenantId, agentType, userId }: {
  tenantId: string; agentType?: string; userId?: string;
}) {
  const { messages } = useI18n();
  const t = messages.continuityObservability.qualification;
  const [end, setEnd] = useState(() => new Date(Date.now() - 60_000).toISOString().slice(0, 16));
  const [start, setStart] = useState(() => new Date(Date.now() - 86_460_000).toISOString().slice(0, 16));
  const [window, setWindow] = useState<{ start: string; end: string } | null>(null);
  const [validationError, setValidationError] = useState(false);
  const scoped = Boolean(tenantId && agentType && userId);
  const result = useQuery({
    queryKey: ['continuity-qualification', tenantId, agentType, userId, window],
    queryFn: () => continuityObservabilityApi.getQualification({ tenantId, agentType: agentType!, userId: userId! }, window!),
    enabled: scoped && window !== null,
    retry: false,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const first = new Date(`${start}Z`), last = new Date(`${end}Z`);
    if (!Number.isFinite(first.getTime()) || !Number.isFinite(last.getTime()) ||
      first >= last || last.getTime() - first.getTime() > 31 * 86_400_000 || last.getTime() > Date.now()) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    const next = { start: first.toISOString(), end: last.toISOString() };
    if (window?.start === next.start && window?.end === next.end) void result.refetch();
    else setWindow(next);
  };

  return (
    <details className="shrink-0 border-b border-slate-200 bg-white">
      <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
        {t.title}
      </summary>
      <section className="space-y-4 px-6 pb-5" aria-label={t.title}>
        <p className="max-w-4xl text-sm leading-6 text-slate-600">{t.description}</p>
        {!scoped && <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{t.chooseScope}</p>}
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-600">{t.start}<input type="datetime-local" value={start} onChange={event => setStart(event.target.value)} className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" /></label>
          <label className="text-xs text-slate-600">{t.end}<input type="datetime-local" value={end} onChange={event => setEnd(event.target.value)} className="mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" /></label>
          <button type="submit" disabled={!scoped || result.isFetching} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">{t.load}</button>
        </form>
        {validationError && <p role="alert" className="text-sm text-red-700">{t.invalidWindow}</p>}
        {result.isFetching && <p role="status" aria-busy="true" className="text-sm text-slate-500">{t.loading}</p>}
        {result.error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{(result.error as HttpError).statusCode === 403 ? messages.continuityObservability.permissionDenied : t.error}</p>}
        {!result.isFetching && !result.error && result.data && <>
          <p className="break-all text-xs text-slate-500">{userId} · {agentType} · {result.data.scope.window_started_at} → {result.data.scope.window_ended_at}</p>
          <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
            {result.data.metrics.map(metric => (
              <article key={metric.key} className={`min-w-0 rounded-lg border p-4 ${metric.status === 'violation' ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-900">{t.metricNames[metric.key] || metric.key}</h3>
                  <span className={`text-xs ${metric.status === 'violation' ? 'font-semibold text-red-800' : metric.status === 'partial' ? 'text-amber-800' : 'text-slate-500'}`}>{t.states[metric.status]}</span>
                </div>
                <p className="mt-3 text-2xl font-semibold tabular-nums text-slate-950">{displayValue(metric)}</p>
                <p className="mt-2 text-xs leading-5 text-slate-600">{t.inspected}: {metric.inspected} / {metric.eligible} · {t.numerator}: {metric.numerator ?? '—'} · {t.denominator}: {metric.denominator} · {t.unknown}: {metric.unknown} · {t.excluded}: {metric.excluded}</p>
                <details className="mt-3 text-xs text-slate-600">
                  <summary className="cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">{t.evidence}</summary>
                  <p className="mt-2 break-words leading-5">{metric.formula}</p>
                  <p className="mt-2 break-words leading-5">{metric.coverage_note}</p>
                  <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-50 p-2">{JSON.stringify({ counts: metric.counts, evidence: metric.evidence }, null, 2)}</pre>
                </details>
              </article>
            ))}
          </div>
          <p className="text-xs leading-5 text-slate-500">{t.acceptanceBoundary}</p>
        </>}
      </section>
    </details>
  );
}
